/**
 * Asset URL Cache
 *
 * Signed Storage URLs are short-lived (one hour TTL by default). Minting one
 * per request works for a single `<img>` on a single screen but breaks down
 * once the editor opens, switches floors, hovers fixture thumbnails, and
 * stays open for an hour: every consumer ends up minting the same URL,
 * sometimes during a window where the URL is about to expire and load
 * silently fails.
 *
 * This module is the one place that knows the strategy: cache signed URLs
 * by path with their expiry, and re-mint pre-expiry to avoid serving a URL
 * that's about to die under a slow image decode. Consumers just call
 * `getAssetUrl(path)` and trust the result.
 *
 * The cache lives in module-scope memory; signing out clears it so a new
 * user doesn't reuse the previous user's signed URLs. The cache does not
 * persist across reloads — that's correct: signed URLs encode an expiry,
 * stale ones cached in localStorage would be worse than re-minting.
 */

import { planStorage } from './plan-storage';

interface CachedUrl {
  url: string;
  /** Epoch ms when the signed URL stops being valid. */
  expiresAt: number;
  /** In-flight mint, if a request is already underway. */
  promise?: Promise<string>;
}

/** Default TTL of `planStorage.signedUrl` is 1 hour. */
const ASSUMED_TTL_MS = 60 * 60 * 1000;

/**
 * Treat URLs as expired this long before their actual `expiresAt`. Five
 * minutes covers slow image decodes and tab-resume after sleep without
 * risking a 403 mid-render.
 */
const REFRESH_MARGIN_MS = 5 * 60 * 1000;

const cache = new Map<string, CachedUrl>();

const isFresh = (entry: CachedUrl, now: number): boolean =>
  entry.expiresAt - now > REFRESH_MARGIN_MS;

/**
 * Inject a custom minter — used by the test suite to keep coverage on the
 * cache logic itself rather than on Supabase. Pass `null` to restore the
 * default (Supabase) implementation.
 */
let mintImpl: (path: string) => Promise<string> = planStorage.signedUrl;

export const setAssetUrlMinter = (
  next: ((path: string) => Promise<string>) | null,
): void => {
  mintImpl = next ?? planStorage.signedUrl;
};

/**
 * Resolve a Storage path to a signed URL, returning a cached value when one
 * is still safely fresh. In-flight mints are deduplicated so a tight burst
 * of consumers (e.g. multiple `<img>` tags pointing at the same path)
 * doesn't trigger N parallel signing requests.
 */
export const getAssetUrl = async (path: string): Promise<string> => {
  const now = Date.now();
  const existing = cache.get(path);

  if (existing && isFresh(existing, now)) {
    return existing.url;
  }
  if (existing?.promise) {
    return existing.promise;
  }

  const promise = mintImpl(path);
  cache.set(path, {
    url: existing?.url ?? '',
    expiresAt: existing?.expiresAt ?? 0,
    promise,
  });

  try {
    const url = await promise;
    cache.set(path, { url, expiresAt: Date.now() + ASSUMED_TTL_MS });
    return url;
  } catch (caught) {
    // Drop the failed in-flight entry so the next caller retries cleanly.
    cache.delete(path);
    throw caught;
  }
};

/** Forget all cached URLs. Call on sign-out so the next user mints fresh. */
export const clearAssetUrlCache = (): void => {
  cache.clear();
};
